// Read-only preview of the service list the agent and the booking desk use. Editing stays in the
// Naturals service list (workspace) or the branch price list (franchise desk).
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getEngagementPortal } from '../engagementPortal';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from '../components/BranchSelect';
import { EmptyState, Loader, PageHead } from '../components/ui';

function editHref() {
  const portal = getEngagementPortal();
  if (portal === 'partner') return '/naturals/partner/price-list';
  if (portal === 'workspace') return '/naturals/workspace/servicelist';
  return '';
}

export default function Services() {
  const { ready, scopeType, branches, selectedIds, branchName, me } = useEngagementScope();
  const showPicker = scopeType !== 'franchise' && branches.length > 1;
  const [salon, setSalon] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!ready || !showPicker) return;
    setSalon((current) => {
      if (current && branches.some((branch) => String(branch.franchise_id) === current)) return current;
      return selectedIds.length === 1 ? selectedIds[0] : '';
    });
  }, [ready, showPicker, branches, selectedIds]);

  const franchiseId = showPicker ? salon : (branches[0] ? String(branches[0].franchise_id) : '');

  useEffect(() => {
    if (!ready || !me || (showPicker && !franchiseId)) {
      setData(null);
      return undefined;
    }
    let alive = true;
    setData(null);
    setError('');
    api
      .listServices(franchiseId || undefined)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message || 'Could not load services');
        toast.error(e.message || 'Could not load services');
      });
    return () => {
      alive = false;
    };
  }, [ready, me, showPicker, franchiseId]);

  const rows = useMemo(() => {
    const list = Array.isArray(data?.services) ? data.services : [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) =>
      [s.name, s.category, s.price_text, ...(s.for || [])].join(' ').toLowerCase().includes(needle),
    );
  }, [data, q]);

  const href = editHref();
  const titleBranch = data?.branch_name || (franchiseId ? branchName(franchiseId) : '');

  return (
    <div>
      <PageHead
        title="Services"
        sub={
          titleBranch
            ? `What the agent can offer at ${titleBranch}. Prices are exactly as stored. This screen does not edit them.`
            : 'What the agent can offer at a branch. Prices are exactly as stored. This screen does not edit them.'
        }
        action={
          href ? (
            <Link to={href} className="de-btn de-btn--outline">
              Edit in the service list
            </Link>
          ) : null
        }
      />

      {showPicker ? (
        <div className="de-row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <span className="de-small de-muted">Salon</span>
          <div style={{ minWidth: 280, maxWidth: 420, flex: '1 1 280px' }}>
            <BranchSelect branches={branches} value={salon} onChange={setSalon} placeholder="Choose a salon" />
          </div>
        </div>
      ) : null}

      {!ready ? <Loader /> : null}

      {ready && showPicker && !salon ? (
        <EmptyState
          icon="💇"
          title="Choose a salon"
          body="Each salon has its own service list. Choose one above to see what the agent offers there."
        />
      ) : null}

      {ready && (!showPicker || salon) && !data && !error ? <Loader text="Loading services…" /> : null}

      {error ? <div className="de-callout de-callout--danger">{error}</div> : null}

      {data ? (
        <div className="de-card">
          <div className="de-row de-row--between de-row--wrap" style={{ gap: 12, marginBottom: 12 }}>
            <div className="de-small de-muted">
              {q.trim() ? `${rows.length} of ${data.count}` : data.count} service{data.count === 1 ? '' : 's'}
              {data.list_id ? ` · list ${data.list_id}` : ''}
            </div>
            <input
              className="de-input de-input--bordered"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, category, or price"
              style={{ maxWidth: 280 }}
            />
          </div>
          {rows.length === 0 ? (
            <EmptyState
              icon="💇"
              title={q ? 'No matching services' : 'No services on this list'}
              body={q ? 'Try another word.' : 'Add services in the Naturals service list. The agent cannot offer a service that is not listed.'}
            />
          ) : (
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Category</th>
                    <th>For</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.service_id}>
                      <td>
                        <div className="de-td-strong">{s.name}</div>
                        {s.description ? <div className="de-small de-muted">{s.description}</div> : null}
                      </td>
                      <td>{s.category || '—'}</td>
                      <td>{(s.for || []).join(', ') || 'everyone'}</td>
                      <td>{s.price_text || 'no price listed'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
